/**
 * Product Comparison Component
 * Visual product picker, spec/feature comparison from metaobject data.
 */

function convertKmToMi(kmValue) {
	const miValue = kmValue * 0.621371;
	if (Math.floor(miValue * 100) % 100 > 85) return Math.ceil(miValue).toFixed(0);
	if (Math.floor(miValue * 100) % 100 > 5) return Math.floor(miValue).toFixed(0) + '.5';
	return miValue % 1 === 0 ? miValue.toFixed(0) : miValue.toFixed(1);
}

function formatPrice(cents) {
	return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(cents / 100);
}

function isNum(v) { return Number.isFinite(parseInt(v)) && !isNaN(v); }

function parseDataTable(el) {
	if (!el) throw new Error('No data table element');
	return JSON.parse(el.textContent);
}

function findEntry(entries, id) { return entries.find((e) => e.id === id) || null; }

// ── Abstract Component ──
class AbstractComponent {
	constructor(el, data) {
		this._container = document.querySelector('[js-comparison="container"]');
		this.el = el;
		this._data = data;
		this._handleOption = this._handleOption.bind(this);
	}
	init() { this._container.addEventListener('option:change', this._handleOption); }
	_handleOption({ detail: { id, entry, variant } }) {
		if (id !== Number(this.el.dataset.id)) return;
		this._update(entry, variant);
	}
	_update() {}
}

// ── Visual Product Picker ──
class PickerComponent {
	constructor(triggerEl, data, columnId) {
		this._container = document.querySelector('[js-comparison="container"]');
		this._trigger = triggerEl;
		this._data = data;
		this._columnId = columnId;
		this._isOpen = false;
		this._activeFamily = null;
		this._selectedEntryId = null;

		const wrapper = triggerEl.closest('.product-comparison__selector-wrapper');
		this._label = wrapper.querySelector("[js-comparison-picker='label']");
		this._panel = wrapper.querySelector("[js-comparison-picker='panel']");

		this._wrapper = triggerEl.closest('.product-comparison__selector-wrapper');
		this._handleTriggerClick = this._handleTriggerClick.bind(this);
		this._handleOutsideClick = this._handleOutsideClick.bind(this);

		this._buildPanel();
		this._trigger.addEventListener('click', this._handleTriggerClick);
		document.addEventListener('click', this._handleOutsideClick);

		// Listen for external updates
		this._container.addEventListener('option:change', (e) => {
			if (e.detail.id !== this._columnId) return;
			this._selectedEntryId = e.detail.entry.id;
			this._label.textContent = e.detail.entry.product.title;
			this._updateSelection();
		});
	}

	_buildPanel() {
		this._panel.innerHTML = '';

		const container = document.createElement('div');
		container.className = 'product-comparison__picker-inner';

		this._data.families.forEach((family, famIndex) => {
			const familyEntries = this._data.entries.filter((e) => e.family === family);
			if (familyEntries.length === 0) return;

			if (this._data.families.length > 1) {
				const details = document.createElement('details');
				details.className = 'product-comparison__picker-group';
				if (famIndex === 0) details.open = true;

				// Accordion — collapse others when one opens
				details.addEventListener('toggle', () => {
					if (details.open) {
						container.querySelectorAll('.product-comparison__picker-group').forEach((d) => {
							if (d !== details) d.open = false;
						});
					}
				});

				const summary = document.createElement('summary');
				summary.className = 'product-comparison__picker-heading';
				summary.innerHTML = family + '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>';

				details.appendChild(summary);
				this._buildFamilyList(details, familyEntries);
				container.appendChild(details);
			} else {
				this._buildFamilyList(container, familyEntries);
			}
		});

		this._panel.appendChild(container);
	}

	_buildFamilyList(parent, entries) {
		entries.forEach((entry) => {
			const btn = document.createElement('button');
			btn.className = 'product-comparison__picker-option';
			btn.type = 'button';
			btn.dataset.entryId = entry.id;

			if (entry.id === this._selectedEntryId) {
				btn.classList.add('product-comparison__picker-option--selected');
			}

			const imgSrc = (entry.product.variants[0] && entry.product.variants[0].featuredImage)
				|| entry.product.featuredImage || '';

			const img = document.createElement('img');
			img.src = imgSrc ? imgSrc + '&width=120' : '';
			img.alt = entry.product.title;
			img.loading = 'lazy';

			const name = document.createElement('span');
			name.className = 'product-comparison__picker-option-name';
			name.textContent = entry.product.title;

			btn.appendChild(img);
			btn.appendChild(name);
			btn.addEventListener('click', () => this._selectEntry(entry));
			parent.appendChild(btn);
		});
	}

	_selectEntry(entry) {
		this._selectedEntryId = entry.id;
		this._label.textContent = entry.product.title;
		this._close();

		const variant = entry.product.variants.find((v) => v.available) || entry.product.variants[0];
		this._container.dispatchEvent(
			new CustomEvent('option:change', {
				detail: { id: this._columnId, entry, variant },
			})
		);

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'selectChangeVariant',
			selectCustomData: [{ product: { handle: entry.product.handle, id: entry.product.id, title: entry.product.title } }],
			selectName: 'picker-' + this._columnId,
			selectValue: entry.id,
		});
	}

	_updateSelection() {
		this._panel.querySelectorAll('.product-comparison__picker-option').forEach((opt) => {
			opt.classList.toggle(
				'product-comparison__picker-option--selected',
				opt.dataset.entryId === this._selectedEntryId
			);
		});
	}

	_handleTriggerClick(e) {
		e.stopPropagation();
		if (this._isOpen) { this._close(); } else { this._open(); }
	}

	_handleOutsideClick(e) {
		if (this._isOpen && !this._panel.contains(e.target) && !this._trigger.contains(e.target)) {
			this._close();
		}
	}

	_open() {
		// Close all other panels
		document.querySelectorAll('.product-comparison__picker-panel').forEach((p) =>
			p.classList.add('product-comparison__picker-panel--hidden')
		);
		document.querySelectorAll('.product-comparison__picker-trigger').forEach((t) =>
			t.classList.remove('product-comparison__picker-trigger--open')
		);

		this._panel.classList.remove(
			'product-comparison__picker-panel--hidden',
			'product-comparison__picker-panel--right',
			'product-comparison__picker-panel--up'
		);
		this._trigger.classList.add('product-comparison__picker-trigger--open');
		this._isOpen = true;
		this._updateSelection();

		// Position after render so we can measure
		requestAnimationFrame(() => this._positionPanel());
	}

	_close() {
		this._panel.classList.add('product-comparison__picker-panel--hidden');
		this._trigger.classList.remove('product-comparison__picker-trigger--open');
		this._panel.style.width = '';
		this._panel.style.minWidth = '';
		this._isOpen = false;
	}

	_positionPanel() {
		this._panel.classList.remove('product-comparison__picker-panel--right', 'product-comparison__picker-panel--up');
		this._panel.style.width = '';
		this._panel.style.minWidth = '';

		const triggerRect = this._trigger.getBoundingClientRect();
		const panelRect = this._panel.getBoundingClientRect();
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		// Overflow right? Align to right edge
		if (triggerRect.left + panelRect.width > vw - 16) {
			this._panel.classList.add('product-comparison__picker-panel--right');
		}

		// Overflow bottom? Open upward
		if (triggerRect.bottom + panelRect.height > vh - 16 && triggerRect.top > panelRect.height) {
			this._panel.classList.add('product-comparison__picker-panel--up');
		}

		// Clamp width to viewport
		const maxW = vw - 32;
		if (panelRect.width > maxW) {
			this._panel.style.width = maxW + 'px';
			this._panel.style.minWidth = 'auto';
		}
	}

	initDefault(entry) {
		this._selectedEntryId = entry.id;
		this._label.textContent = entry.product.title;

		const variant = entry.product.variants.find((v) => v.available) || entry.product.variants[0];
		this._container.dispatchEvent(
			new CustomEvent('option:change', { detail: { id: this._columnId, entry, variant } })
		);
	}
}

// ── Column Component ──
class ColumnComponent extends AbstractComponent {
	constructor(el, data) {
		super(el, data);
		this._els = {
			img: el.querySelector("[js-comparison-card='img']"),
			imgAlt: el.querySelector("[js-comparison-card='imgAlt']"),
			imgContainer: el.querySelector("[js-comparison-card='image']"),
			imgLink: el.querySelector("[js-comparison-card='imageLink']"),
			viewLink: el.querySelector("[js-comparison-card='viewLink']"),
		};
		this.init();
	}

	_update(entry, variant) {
		const primaryImage = variant.featuredImage
			|| (entry.product.variants[0] && entry.product.variants[0].featuredImage)
			|| entry.product.featuredImage;
		this._setImg(this._els.img, primaryImage);
		this._setImg(this._els.imgAlt, entry.product.altImage || '');
		this._els.viewLink.href = entry.product.url;
		this._els.imgLink.href = entry.product.url;
	}

	_setImg(imgEl, src) {
		if (!imgEl || !src) { if (imgEl) imgEl.style.display = 'none'; return; }
		imgEl.style.display = '';
		const widths = [352, 832, 1200, 1920];
		imgEl.src = src;
		imgEl.srcset = widths.map((w) => src + '&width=' + w + ' ' + w + 'w').join(',');
	}
}

// ── Spec Component ──
class SpecComponent extends AbstractComponent {
	constructor(el, data) {
		super(el, data);
		this._els = {
			maxRange: el.querySelector("[js-comparison-spec='maxRange']"),
			motorPower: el.querySelector("[js-comparison-spec='motorPower']"),
			topSpeed: el.querySelector("[js-comparison-spec='topSpeed']"),
			rideComfort: el.querySelector("[js-comparison-spec='rideComfort']"),
			hillClimbing: el.querySelector("[js-comparison-spec='hillClimbing']"),
			unitSelect: document.querySelector("[js-comparison='measurementUnit']"),
		};
		this._unit = 'km';
		this._entry = null;
		this._variant = null;
		this._handleUnit = this._handleUnit.bind(this);
		if (this._els.unitSelect) this._els.unitSelect.addEventListener('change', this._handleUnit);
		this.init();
	}

	_handleUnit(e) {
		this._unit = e.target.value;
		if (this._entry) {
			this._setRange(this._entry.range);
			this._setSpeed(this._entry.topSpeed);
		}
	}

	_update(entry, variant) {
		this._entry = entry;
		this._variant = variant;

		this._setRange(entry.range);
		this._setPower(entry.power);
		this._setSpeed(entry.topSpeed);
		this._setBar(this._els.rideComfort, entry.rideComfort);
		this._setBar(this._els.hillClimbing, entry.hillClimbing);
	}

	_setRange(v) {
		if (!isNum(v) || v < 0) { this._els.maxRange.textContent = '-'; this._els.maxRange.dataset.value = '-1'; return; }
		this._els.maxRange.dataset.value = v.toString();
		this._els.maxRange.textContent = this._unit === 'mi' ? convertKmToMi(v) + ' mi' : v + ' km';
	}

	_setPower(v) {
		if (!isNum(v) || v < 0) { this._els.motorPower.textContent = '-'; this._els.motorPower.dataset.value = '-1'; return; }
		this._els.motorPower.dataset.value = v.toString();
		this._els.motorPower.textContent = v + ' W';
	}

	_setSpeed(v) {
		if (!isNum(v) || v < 0) { this._els.topSpeed.textContent = '-'; this._els.topSpeed.dataset.value = '-1'; return; }
		this._els.topSpeed.dataset.value = v.toString();
		this._els.topSpeed.textContent = this._unit === 'mi' ? convertKmToMi(v) + ' mph' : v + ' km/h';
	}

	_setBar(barEl, value) {
		if (!barEl || !isNum(value) || value < 0) return;
		value = Math.min(Math.abs(value), 10);
		barEl.dataset.value = value.toString();
		barEl.querySelectorAll('span').forEach((s, i) => {
			s.classList.toggle('product-comparison__spec-bar-active', i < value);
		});
	}
}

// ── Feature Component ──
// Feature rows are pre-built in Liquid. JS just toggles data-value per entry.
class FeatureComponent extends AbstractComponent {
	constructor(el, data) {
		super(el, data);
		this._items = el.querySelectorAll("[js-comparison-feature='item']");
		this.init();
	}

	_update(entry) {
		const set = entry.featureSet || [];
		this._items.forEach((item) => {
			item.dataset.value = set.includes(item.dataset.featureName).toString();
		});
	}
}

// ── Info Component ──
class InfoComponent extends AbstractComponent {
	constructor(el, data) {
		super(el, data);
		this._toggle = el.querySelector("[js-comparison-tooltip='toggle']");
		this._tooltip = el.querySelector("[js-comparison-tooltip='tooltip']");
		this._key = el.dataset.targetKey || '';
		this._open = false;
		if (this._toggle) {
			this._toggle.addEventListener('mouseover', () => { if (window.innerWidth > 1025) this._show(true); });
			this._toggle.addEventListener('mouseout', () => { if (window.innerWidth > 1025) this._show(false); });
			this._toggle.addEventListener('click', () => { if (window.innerWidth < 1025) this._show(!this._open); });
			this._toggle.addEventListener('blur', () => { if (window.innerWidth < 1025) this._show(false); });
		}
		this.init();
	}
	_update(entry) {
		const has = (entry.featureSet || []).includes(this._key);
		this.el.classList.toggle('product-comparison__info--hidden', !has);
	}
	_show(v) {
		this._open = v;
		if (this._tooltip) this._tooltip.classList.toggle('product-comparison__info-tooltip--hidden', !v);
	}
}

// ── Best specs highlight ──
function emphasiseBestSpecs(specEls) {
	const keys = ['maxRange', 'motorPower', 'topSpeed', 'rideComfort', 'hillClimbing'];
	const len = window.innerWidth >= 990 ? specEls.length : Math.min(specEls.length, 2);
	keys.forEach((key) => {
		let max = -Infinity, best = [];
		for (let i = 0; i < len; i++) {
			const el = specEls[i]?.querySelector(`[js-comparison-spec="${key}"]`);
			if (!el) continue;
			el.parentElement?.classList.remove('product-comparison__spec-item--best');
			const v = Number(el.dataset.value);
			if (v > max) { max = v; best = [i]; } else if (v === max) { best.push(i); }
		}
		if (best.length < len) {
			best.forEach((idx) => {
				specEls[idx]?.querySelector(`[js-comparison-spec="${key}"]`)
					?.parentElement?.classList.add('product-comparison__spec-item--best');
			});
		}
	});
}

// ── Init ──
document.addEventListener('DOMContentLoaded', () => {
	const container = document.querySelector('[js-comparison="container"]');
	const dataEl = document.querySelector('[js-comparison="dataTable"]');
	if (!container || !dataEl) return;

	const data = parseDataTable(dataEl);
	data.entries = data.entries.filter((e) => e.product && e.product.id);

	const specEls = document.querySelectorAll('[js-comparison="spec"]');
	const columnLinks = document.querySelectorAll('[js-comparison-card="viewLink"]');
	const selectorContainer = document.querySelector('[js-comparison="selectorContainer"]');

	const selectedUrls = {};
	const pricingCols = document.querySelectorAll('[js-comparison="pricingCol"]');

	// Columns
	document.querySelectorAll('[js-comparison="card"]').forEach((el) => new ColumnComponent(el, data));

	// Specs
	specEls.forEach((el) => new SpecComponent(el, data));

	// Features
	document.querySelectorAll('[js-comparison="feature"]').forEach((el) => new FeatureComponent(el, data));

	// Info tooltips
	document.querySelectorAll('[js-comparison-tooltip="container"]').forEach((el) => new InfoComponent(el, data));

	// Option change listener
	container.addEventListener('option:change', (e) => {
		const { id, entry, variant } = e.detail;
		selectedUrls[id] = entry.product.url;

		// Update pricing section
		pricingCols.forEach((col) => {
			if (Number(col.dataset.id) !== id) return;
			const priceEl = col.querySelector("[js-comparison-pricing='price']");
			const ratingEl = col.querySelector("[js-comparison-pricing='reviewRating']");
			const countEl = col.querySelector("[js-comparison-pricing='reviewCount']");
			const buyEl = col.querySelector("[js-comparison-pricing='buyLink']");

			if (priceEl) {
				const cmp = variant.compareAtPrice;
				if (cmp && cmp > 0 && cmp !== variant.price) {
					priceEl.innerHTML = formatPrice(variant.price) + ' <s style="opacity:0.5;font-weight:400">' + formatPrice(cmp) + '</s>';
				} else {
					priceEl.textContent = formatPrice(variant.price);
				}
			}
			const rating = Math.round((entry.product.reviews.rating || 0) * 10) / 10;
			const count = entry.product.reviews.count || 0;
			if (ratingEl) ratingEl.textContent = rating.toFixed(1);
			if (countEl) countEl.textContent = count > 0 ? '(' + count + ')' : '';
			if (buyEl) buyEl.href = entry.product.url;
		});

		emphasiseBestSpecs(specEls);
	});

	// Visual pickers
	const pickers = [];
	document.querySelectorAll('[js-comparison="pickerTrigger"]').forEach((triggerEl) => {
		const colId = Number(triggerEl.dataset.id);
		const picker = new PickerComponent(triggerEl, data, colId);
		pickers.push({ picker, colId });
	});

	// Set defaults from query params
	// Supports: ?product=handle (first column)
	//           ?product1=handle&product2=handle&product3=handle (specific columns)
	//           ?variant=handle (legacy, first column)
	const isMobile = window.innerWidth < 990;
	const maxCols = isMobile ? 2 : 3;
	const params = new URLSearchParams(window.location.search);

	function findEntryByParam(val) {
		if (!val) return null;
		// Try handle match
		let entry = data.entries.find((e) => e.product.handle === val);
		if (entry) return entry;
		// Try metaobject handle match
		entry = data.entries.find((e) => e.id === val);
		if (entry) return entry;
		// Try product ID match
		entry = data.entries.find((e) => String(e.product.id) === val);
		return entry || null;
	}

	const usedEntryIds = new Set();

	pickers.forEach(({ picker, colId }) => {
		if (colId >= maxCols) return;
		let entry = null;

		// Check column-specific param first: ?product1=, ?product2=, ?product3=
		const colParam = params.get('product' + (colId + 1));
		if (colParam) {
			entry = findEntryByParam(colParam);
		}

		// Fallback: ?product= or ?variant= for first column
		if (!entry && colId === 0) {
			const singleParam = params.get('product') || params.get('variant');
			if (singleParam) entry = findEntryByParam(singleParam);
		}

		// Skip if already used by another column
		if (entry && usedEntryIds.has(entry.id)) entry = null;

		// Default: pick next unused entry
		if (!entry) {
			entry = data.entries.find((e) => !usedEntryIds.has(e.id));
		}

		if (entry) {
			usedEntryIds.add(entry.id);
			picker.initDefault(entry);
		}
	});

	// Measurement unit GTM
	const unitSelect = document.querySelector("[js-comparison='measurementUnit']");
	if (unitSelect) {
		unitSelect.addEventListener('change', (e) => {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({ event: 'selectChangeMeasurement', selectName: 'measurementUnit', selectValue: e.target.value });
		});
	}

	// Sticky selector
	function updateSticky() {
		const h = window.innerWidth > 1025 ? 71 : 61;
		const isSticky = selectorContainer.getBoundingClientRect().top - h <= 0;
		selectorContainer.classList.toggle('product-comparison__selectors--sticky', isSticky);
	}

	window.addEventListener('load', updateSticky);
	window.addEventListener('resize', updateSticky);
	window.addEventListener('scroll', updateSticky, { passive: true });
});
